import { useState, useEffect, useMemo } from 'react';
import {
  FaTimes,
  FaPlay,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEdit,
  FaCode,
  FaSave,
  FaSearch,
  FaCopy,
  FaDatabase,
  FaLayerGroup,
} from 'react-icons/fa';
import { interoperabilityApi } from '../../api/interoperability.api';
import { cn } from '../../../../utils/cn';
import { TABS_API_DETAILS, ENUM_TABS_API_DETAILS } from '../../interfaces/services.interface';
import Select from 'react-select';
import { XSD_TYPES } from '../../interfaces/services.interface';
import { DataTable } from '../../../../components/DataTable/DataTable';
import styles from '../../styles/detailsModal.module.css';
function ApiDetailModal({ onClose, api }) {
  const [activeTab, setActiveTab] = useState(ENUM_TABS_API_DETAILS.CONNECTION); // "connection" | "response" | "fields"
  const [payloadStr, setPayloadStr] = useState('{}');
  const [liveResponse, setLiveResponse] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [executedSuccess, setExecutedSuccess] = useState(null);
  const [latency, setLatency] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [payloadError, setPayloadError] = useState(false);
  const [copied, setCopied] = useState(false);

  // Campos descubiertos para XSD
  const [discoveredFields, setDiscoveredFields] = useState([]);
  const [savingFields, setSavingFields] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');

  const loadInitialData = async (serviceCode) => {
    try {
      const existing = await interoperabilityApi.getFieldMappings(serviceCode);
      if (Array.isArray(existing) && existing.length > 0) {
        setDiscoveredFields(existing);
      }
    } catch {
      // Silencioso si aún no tiene mappings
    }
  };

  useEffect(() => {
    if (api) {
      setPayloadStr(JSON.stringify(api.request || {}, null, 2));
      setLiveResponse(api.response || null);
      setExecutedSuccess(null);
      setLatency(null);
      setStatusCode(null);
      setStatusMessage(null);
      setPayloadError(false);
      setSavedSuccess(false);
      setActiveTab(ENUM_TABS_API_DETAILS.CONNECTION);
      loadInitialData(api.code || api.name);
    }
  }, [api]);

  const filteredFields = useMemo(() => {
    if (!fieldSearch.trim()) return discoveredFields;
    const q = fieldSearch.toLowerCase();
    return discoveredFields.filter(
      (f) =>
        f.sourceFieldName?.toLowerCase().includes(q) ||
        f.targetFieldName?.toLowerCase().includes(q) ||
        f.label?.toLowerCase().includes(q) ||
        f.jsonPath?.toLowerCase().includes(q)
    );
  }, [discoveredFields, fieldSearch]);

  const selectedCount = useMemo(() => {
    return discoveredFields.filter((f) => f.enabled).length;
  }, [discoveredFields]);

  if (!api) return null;

  const handleRunConnectionTest = async () => {
    let payload = {};
    try {
      payload = JSON.parse(payloadStr);
      setPayloadError(false);
    } catch {
      setPayloadError(true);
      return;
    }

    setExecuting(true);
    setExecutedSuccess(null);
    setSavedSuccess(false);

    try {
      const result = await interoperabilityApi.testConnection(api.code || api.name, {
        requestPayload: payload,
        endpointUrl: api.endpointUrl,
        method: api.method,
        protocolType: api.protocolType,
        authType: api.authType,
        authToken: api.authToken,
        timeoutMs: api.timeoutMs || 5000,
      });

      setLiveResponse(result.rawResponse || result);
      setExecutedSuccess(result.success !== false);
      setLatency(result.latencyMs ?? null);
      setStatusCode(result.statusCode ?? 200);
      setStatusMessage(result.statusMessage || 'Conexión exitosa');

      if (Array.isArray(result.discoveredFields) && result.discoveredFields.length > 0) {
        // Combinar con selecciones existentes si ya había campos
        setDiscoveredFields((prev) => {
          const mapPrev = new Map(prev.map((p) => [p.sourceFieldName, p]));
          return result.discoveredFields.map((df) => {
            const old = mapPrev.get(df.sourceFieldName);
            if (old) {
              return {
                ...df,
                enabled: old.enabled,
                targetFieldName: old.targetFieldName || df.targetFieldName,
                label: old.label || df.label,
                xsdType: old.xsdType || df.xsdType,
              };
            }
            return { ...df, enabled: true };
          });
        });
      }
    } catch (err) {
      setLiveResponse({ error: 'Fallo en la prueba de conexión', detalle: err.message });
      setExecutedSuccess(false);
      setStatusCode(500);
      setStatusMessage(err.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleSaveFieldsForXsd = async () => {
    if (!api.code && !api.name) return;
    setSavingFields(true);
    setSavedSuccess(false);

    try {
      const saved = await interoperabilityApi.saveFieldMappings(
        api.code || api.name,
        discoveredFields
      );
      if (Array.isArray(saved)) {
        setDiscoveredFields(saved);
      }
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 4000);
    } catch (err) {
      alert('Error al guardar selección de campos para XSD: ' + err.message);
    } finally {
      setSavingFields(false);
    }
  };

  const toggleField = (index, val) => {
    setDiscoveredFields((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], enabled: val !== undefined ? val : !next[index].enabled };
      return next;
    });
  };

  const updateFieldProperty = (index, key, val) => {
    setDiscoveredFields((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: val };
      return next;
    });
  };

  const selectAllFields = (select) => {
    setDiscoveredFields((prev) => prev.map((f) => ({ ...f, enabled: select })));
  };

  const copyResponseJson = () => {
    if (!liveResponse) return;
    navigator.clipboard.writeText(JSON.stringify(liveResponse, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const columns = [
    {
      accessorKey: 'sourceFieldName',
      header: 'XSD',
      cell: ({ row }) => {
        const item = row.original;
        const originalIndex = discoveredFields.findIndex(
          (x) => x.sourceFieldName === item.sourceFieldName
        );
        return (
          <input
            type="checkbox"
            checked={!!item.enabled}
            onChange={(e) => toggleField(originalIndex, e.target.checked)}
            className={styles.sourceFieldName}
          />
        );
      },
    },
    {
      accessorKey: 'jsonPath',
      header: 'CAMPO ORIGEN / JSONPath',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <>
            <div className={styles.divJsonPath}>{item.sourceFieldName}</div>
            {item.jsonPath && <div className={styles.div2JsonPath}>{item.jsonPath}</div>}
          </>
        );
      },
    },
    {
      accessorKey: 'rfc',
      header: 'NOMBRE DESTINO XSD',
      cell: ({ row }) => {
        const item = row.original;
        const originalIndex = discoveredFields.findIndex(
          (x) => x.sourceFieldName === item.sourceFieldName
        );
        return (
          <input
            type="text"
            value={item.targetFieldName || ''}
            onChange={(e) => updateFieldProperty(originalIndex, 'targetFieldName', e.target.value)}
            className={styles.inputTable}
          />
        );
      },
    },
    {
      accessorKey: 'organismo',
      header: 'ETIQUETA DESCRIPTIVA',
      cell: ({ row }) => {
        const item = row.original;
        const originalIndex = discoveredFields.findIndex(
          (x) => x.sourceFieldName === item.sourceFieldName
        );
        return (
          <input
            type="text"
            value={item.label || ''}
            onChange={(e) => updateFieldProperty(originalIndex, 'label', e.target.value)}
            className={styles.inputTable}
          />
        );
      },
    },
    {
      accessorKey: 'xsdType',
      header: 'TIPO XSD',
      size: 300,

      cell: ({ row }) => {
        const item = row.original;

        const originalIndex = discoveredFields.findIndex(
          (x) => x.sourceFieldName === item.sourceFieldName
        );

        return (
          <Select
            className="w-full"
            options={XSD_TYPES}
            getOptionLabel={(option) => option.value}
            getOptionValue={(option) => option.key}
            value={XSD_TYPES.find((option) => option.key === item.xsdType) || null}
            onChange={(option) => updateFieldProperty(originalIndex, 'xsdType', option?.key ?? '')}
            placeholder="Todos los tipos"
            isSearchable={false}
          />
        );
      },
    },
    {
      accessorKey: 'sampleValue',
      header: 'VALOR MUESTRA',
      cell: ({ getValue }) => {
        const value = getValue();

        return (
          <span className={styles.sampleValue} title={value || ''}>
            {value || '—'}
          </span>
        );
      },
    },
    {
      accessorKey: 'isRequired',
      header: 'REQ',
      cell: ({ row }) => {
        const item = row.original;
        const originalIndex = discoveredFields.findIndex(
          (x) => x.sourceFieldName === item.sourceFieldName
        );
        return (
          <input
            type="checkbox"
            checked={!!item.isRequired}
            onChange={(e) => updateFieldProperty(originalIndex, 'isRequired', e.target.checked)}
            className={styles.isRequired}
          />
        );
      },
    },
  ];

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-sizes">
        {/* Header */}
        <div className="modal-header">
          <div>
            <div className={styles.divModalTitle}>
              <h2 className={styles.modalTitle}>{api.name}</h2>
              <span className={styles.spanProtocolType}>{api.protocolType || 'REST'}</span>
              <span className={styles.spanMethod}>{api.method || 'POST'}</span>
            </div>
            <span className={styles.spanDetails}>
              {api.institution} · {api.version} · {api.endpointUrl || 'Adaptador de integración'}
            </span>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Status Bar */}
        <div className={styles.divStatusBar}>
          <div className={styles.divStatus}>
            {executedSuccess === true && (
              <span className={styles.divStatus}>
                <FaCheckCircle className={styles.iconFaCheckCircle} /> {statusCode} {statusMessage}
              </span>
            )}
            {executedSuccess === false && (
              <span className={styles.fail}>
                <FaExclamationTriangle className={styles.iconFaExclamationTriangle} />{' '}
                {statusCode || 'ERROR'} {statusMessage}
              </span>
            )}
            {latency !== null && <span className={styles.spanLatency}>{latency} ms</span>}
          </div>
          <div className={styles.divFieldsXsd}>
            <span className={styles.spanXsd}>Campos para XSD:</span>
            <span className={styles.numberFields}>
              {selectedCount} de {discoveredFields.length}
            </span>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="tabs-bar">
          {TABS_API_DETAILS.map((tab) => (
            <button
              key={tab.enum}
              type="button"
              onClick={() => setActiveTab(tab.enum)}
              className={`tab-item  ${activeTab == tab.enum ? 'tab-item-active' : ''}`}
            >
              {tab.icon}
              {tab.id}
            </button>
          ))}
        </div>

        {/* Tab Content */}

        <div className={styles.divTabs}>
          {/* TAB 1: CONEXIÓN */}
          {activeTab === ENUM_TABS_API_DETAILS.CONNECTION && (
            <div className={styles.connectionTab}>
              <div className={styles.payloadCard}>
                <div className={styles.payloadHeader}>
                  <label className={styles.payloadLabel}>
                    <FaEdit />
                    Parámetros de Prueba (JSON Payload)
                  </label>

                  <span className={styles.payloadHint}>
                    Edita los parámetros de búsqueda o consulta
                  </span>
                </div>

                {payloadError && (
                  <div className={styles.payloadError}>
                    Sintaxis JSON inválida. Corrige el formato antes de ejecutar.
                  </div>
                )}

                <textarea
                  value={payloadStr}
                  onChange={(e) => {
                    setPayloadStr(e.target.value);
                    setPayloadError(false);
                  }}
                  rows={8}
                  spellCheck={false}
                  className={cn(
                    styles.payloadTextarea,
                    payloadError && styles.payloadTextareaError
                  )}
                />
              </div>

              <div className={styles.connectionActions}>
                <div className={styles.connectionDescription}>
                  <span>
                    Al probar la conexión, el motor inspeccionará automáticamente{' '}
                    <strong>todos los campos posibles</strong> (incluyendo colecciones, listas y
                    objetos anidados).
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleRunConnectionTest}
                  disabled={executing}
                  className={cn('btn-primary', styles.connectionButton)}
                >
                  <FaPlay className={executing ? styles.executingIcon : ''} />
                  {executing
                    ? 'Conectando e Introspeccionando...'
                    : 'Ejecutar Conexión e Introspección'}
                </button>
              </div>

              {discoveredFields.length > 0 && (
                <div className={styles.discoveredFieldsBanner}>
                  <div className={styles.discoveredFieldsInfo}>
                    <div className={styles.discoveredFieldsIcon}>
                      <FaDatabase />
                    </div>

                    <div className={styles.discoveredFieldsText}>
                      <h4 className={styles.discoveredFieldsTitle}>
                        {discoveredFields.length} campos detectados en la colección
                      </h4>

                      <span className={styles.discoveredFieldsDescription}>
                        Puedes revisar y filtrar los campos que deseas que estén disponibles en el
                        módulo XSD.
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveTab('fields')}
                    className={styles.viewFieldsButton}
                  >
                    Ver y Filtrar Campos para XSD
                  </button>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: RESPUESTA DE LA COLECCIÓN */}
          {activeTab === ENUM_TABS_API_DETAILS.RESPONSE && (
            <div className={styles.responseTab}>
              <div className={styles.responseHeader}>
                <span className={styles.responseLabel}>
                  <FaCode />
                  Carga Útil Devuelta (Raw JSON Payload)
                </span>

                <button type="button" onClick={copyResponseJson} className={styles.copyJsonButton}>
                  <FaCopy />
                  {copied ? 'Copiado!' : 'Copiar JSON'}
                </button>
              </div>

              <pre className={styles.responsePayload}>
                {liveResponse
                  ? JSON.stringify(liveResponse, null, 2)
                  : "Sin respuesta aún. Haz clic en 'Probar Conexión'."}
              </pre>
            </div>
          )}

          {/* TAB 3: CAMPOS Y FILTRO PARA XSD */}
          {activeTab === ENUM_TABS_API_DETAILS.FIELDS && (
            <div className={styles.fieldsTab}>
              {/* Barra de ayuda y acciones de guardado */}
              <div className={styles.fieldsHelpBanner}>
                <div className={styles.fieldsHelpInfo}>
                  <div className={styles.fieldsHelpIcon}>
                    <FaLayerGroup />
                  </div>

                  <div className={styles.fieldsHelpText}>
                    <h4 className={styles.fieldsHelpTitle}>
                      Mapeo de Datos para Creación de XSD y Certificados
                    </h4>

                    <span className={styles.fieldsHelpDescription}>
                      Activa las casillas de los campos que deseas exponer al creador de XSD. Puedes
                      editar el nombre final y tipo de dato.
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSaveFieldsForXsd}
                  disabled={savingFields}
                  className={cn('btn-primary', styles.saveFieldsButton)}
                >
                  <FaSave />
                  {savingFields ? 'Guardando...' : 'Guardar Campos Seleccionados para XSD'}
                </button>
              </div>

              {savedSuccess && (
                <div className={styles.savedSuccessMessage}>
                  <FaCheckCircle className={styles.savedSuccessIcon} />
                  Los campos seleccionados se han guardado exitosamente. Ahora están disponibles en
                  el constructor de XSD.
                </div>
              )}

              {/* Filtros y selección masiva */}
              <div className={styles.fieldsToolbar}>
                <div className={styles.fieldSearchContainer}>
                  <FaSearch className={styles.fieldSearchIcon} />

                  <input
                    type="text"
                    placeholder="Buscar campo..."
                    value={fieldSearch}
                    onChange={(e) => setFieldSearch(e.target.value)}
                    className={styles.fieldSearchInput}
                  />
                </div>

                <div className={styles.bulkSelectionActions}>
                  <button
                    type="button"
                    onClick={() => selectAllFields(true)}
                    className={styles.bulkSelectionButton}
                  >
                    Seleccionar Todos
                  </button>

                  <button
                    type="button"
                    onClick={() => selectAllFields(false)}
                    className={styles.bulkSelectionButton}
                  >
                    Deseleccionar Todos
                  </button>
                </div>
              </div>

              {/* Tabla de campos */}
              <div className={styles.fieldsTableContainer}>
                <div className={styles.fieldsTableScroll}>
                  <DataTable columns={columns} data={filteredFields} showPagination={false} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`modal-footer ${styles.formFooter}`}>
          <button type="button" onClick={onClose} className="btn-cancel">
            Cerrar
          </button>

          {activeTab === ENUM_TABS_API_DETAILS.FIELDS && (
            <button
              type="button"
              onClick={handleSaveFieldsForXsd}
              disabled={savingFields}
              className={`btn-primary ${styles.footerSaveButton}`}
            >
              <FaSave />
              {savingFields ? 'Guardando...' : 'Guardar Selección para XSD'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ApiDetailModal;
